export const TAU = Math.PI * 2;

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

export function inverseLerp(from: number, to: number, value: number): number {
  return from === to ? 0 : clamp((value - from) / (to - from), 0, 1);
}

export function smoothstep(edge0: number, edge1: number, value: number): number {
  const x = inverseLerp(edge0, edge1, value);
  return x * x * (3 - 2 * x);
}

export function easeOutCubic(value: number): number {
  return 1 - (1 - clamp(value, 0, 1)) ** 3;
}

export function logLerp(from: number, to: number, amount: number): number {
  return Math.exp(lerp(Math.log(from), Math.log(to), clamp(amount, 0, 1)));
}

export function damp(
  current: number,
  target: number,
  smoothing: number,
  deltaSeconds: number,
): number {
  return lerp(current, target, 1 - Math.exp(-smoothing * deltaSeconds));
}

export function formatPhysicalSize(diameterMeters: number): { value: string; unit: string } {
  if (diameterMeters < 0.01) {
    return { value: (diameterMeters * 1000).toFixed(1), unit: "mm" };
  }
  if (diameterMeters < 1) {
    return { value: (diameterMeters * 100).toFixed(diameterMeters < 0.1 ? 1 : 0), unit: "cm" };
  }
  if (diameterMeters < 1000) {
    return { value: diameterMeters.toFixed(diameterMeters < 10 ? 2 : diameterMeters < 100 ? 1 : 0), unit: "m" };
  }
  return { value: (diameterMeters / 1000).toFixed(2), unit: "km" };
}

export function squaredDistance(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return dx * dx + dz * dz;
}

export interface NavigationMetrics {
  readonly distanceMeters: number;
  readonly bearingRadians: number;
}

/**
 * Projects a world-space target into camera-relative navigation coordinates.
 * A bearing of zero means straight ahead and positive values turn right.
 */
export function navigationMetrics(
  playerXMeters: number,
  playerZMeters: number,
  targetXMeters: number,
  targetZMeters: number,
  cameraYaw: number,
): NavigationMetrics {
  const dx = targetXMeters - playerXMeters;
  const dz = targetZMeters - playerZMeters;
  const forward = dx * -Math.sin(cameraYaw) + dz * -Math.cos(cameraYaw);
  const right = dx * Math.cos(cameraYaw) + dz * -Math.sin(cameraYaw);
  return {
    distanceMeters: Math.hypot(dx, dz),
    bearingRadians: Math.atan2(right, forward),
  };
}
